import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SentryErrorFallback from "@/components/SentryErrorFallback";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ImpersonationBanner } from "@/components/platform/ImpersonationBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ControllerLayout } from "@/layouts/ControllerLayout";
import Auth from "./pages/Auth";
import AuthSetup from "./pages/AuthSetup";
import AuthResetPassword from "./pages/AuthResetPassword";
import ControllerDashboard from "./pages/controller/ControllerDashboard";
import Approvals from "./pages/controller/Approvals";
import Projects from "./pages/controller/Projects";
import ProjectDetail from "./pages/controller/ProjectDetail";
import PersonnelProjects from "./pages/controller/PersonnelProjects";
import PersonnelProjectDetail from "./pages/controller/PersonnelProjectDetail";
import Invoices from "./pages/controller/Invoices";
import Periods from "./pages/controller/Periods";
import Audit from "./pages/controller/Audit";
import Settings from "./pages/controller/Settings";
import Customers from "./pages/controller/Customers";
import Vendors from "./pages/controller/Vendors";
import Vendor1099Report from "./pages/controller/Vendor1099Report";
import VendorDetail from "./pages/controller/VendorDetail";
import Compliance from "./pages/controller/Compliance";
import Estimates from "./pages/controller/Estimates";
import Bills from "./pages/controller/Bills";
import Notifications from "./pages/controller/Notifications";
import CustomerDetail from "./pages/controller/CustomerDetail";
import Calendar from "./pages/controller/Calendar";
import Comms from "./pages/controller/Comms";
import Team from "./pages/controller/Team";
import TeamMemberDetail from "./pages/controller/TeamMemberDetail";
import NotFound from "./pages/NotFound";
import EstimateApproval from "./pages/EstimateApproval";
import { WorkerPortalLayout } from "@/layouts/WorkerPortalLayout";
import { ContractLaborPortalLayout } from "@/layouts/ContractLaborPortalLayout";
import { CustomerPortalLayout } from "@/layouts/CustomerPortalLayout";
import ContractLaborDashboard from "./pages/contract-labor/ContractLaborDashboard";
import ContractLaborWorkOrders from "./pages/contract-labor/ContractLaborWorkOrders";
import ContractLaborLogHours from "./pages/contract-labor/ContractLaborLogHours";
import ContractLaborDocuments from "./pages/contract-labor/ContractLaborDocuments";
import WorkerDashboard from "./pages/portal/WorkerDashboard";
import WorkerHours from "./pages/portal/WorkerHours";
import WorkerPayments from "./pages/portal/WorkerPayments";
import WorkerReimbursements from "./pages/portal/WorkerReimbursements";
import WorkerDocuments from "./pages/portal/WorkerDocuments";
import WorkerMessages from "./pages/portal/WorkerMessages";
import WorkerProfile from "./pages/portal/WorkerProfile";
import WorkerProjects from "./pages/portal/WorkerProjects";
import CustomerDashboard from "./pages/portal/CustomerDashboard";
import CustomerProjects from "./pages/portal/CustomerProjects";
import CustomerProjectDetail from "./pages/portal/CustomerProjectDetail";
import CustomerInvoices from "./pages/portal/CustomerInvoices";
import CustomerChangeOrders from "./pages/portal/CustomerChangeOrders";
import CustomerApprovals from "./pages/portal/CustomerApprovals";
import CustomerDocuments from "./pages/portal/CustomerDocuments";
import CustomerProfile from "./pages/portal/CustomerProfile";
import PlatformAdminDashboard from "./pages/platform/PlatformAdminDashboard";
import { PlatformAdminGuard } from "@/components/PlatformAdminGuard";
import { useIsPlatformAdmin } from "@/hooks/usePlatformSummary";
import { useAuth } from "@/contexts/AuthContext";

const queryClient = new QueryClient();

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { data: isAdmin, isLoading } = useIsPlatformAdmin();

  if (loading || (user && isLoading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user && isAdmin) {
    return <Navigate to="/platform" replace />;
  }

  return <Navigate to="/controller" replace />;
};

const App = () => (
  <Sentry.ErrorBoundary fallback={({ error, resetError }) => <SentryErrorFallback error={error as Error} resetError={resetError} />}>
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ThemeProvider>
      <ImpersonationProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ImpersonationBanner />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/setup" element={<AuthSetup />} />
            <Route path="/auth/reset-password" element={<AuthResetPassword />} />
            <Route path="/auth/worker" element={<Navigate to="/auth" replace />} />
            <Route path="/auth/vendor" element={<Navigate to="/auth" replace />} />
            <Route path="/auth/customer" element={<Navigate to="/auth" replace />} />
            <Route path="/estimate/:token" element={<EstimateApproval />} />
            <Route
              path="/controller"
              element={
                <ProtectedRoute portalGuard="controller">
                  <ControllerLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ControllerDashboard />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="comms" element={<Comms />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="personnel" element={<PersonnelProjects />} />
              <Route path="personnel/:id" element={<PersonnelProjectDetail />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="periods" element={<Periods />} />
              <Route path="audit" element={<Audit />} />
              <Route path="team" element={<Team />} />
              <Route path="team/:id" element={<TeamMemberDetail />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="vendors/1099-report" element={<Vendor1099Report />} />
              <Route path="vendors/:id" element={<VendorDetail />} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="bills" element={<Bills />} />
              <Route path="estimates" element={<Estimates />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            {/* Worker Portal */}
            <Route
              path="/portal/worker"
              element={
                <ProtectedRoute portalGuard="worker">
                  <WorkerPortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WorkerDashboard />} />
              <Route path="hours" element={<WorkerHours />} />
              <Route path="payments" element={<WorkerPayments />} />
              <Route path="projects" element={<WorkerProjects />} />
              <Route path="reimbursements" element={<WorkerReimbursements />} />
              <Route path="documents" element={<WorkerDocuments />} />
              <Route path="messages" element={<WorkerMessages />} />
              <Route path="profile" element={<WorkerProfile />} />
              <Route path="comms" element={<Comms />} />
            </Route>
            {/* Contract Labor Portal */}
            <Route
              path="/portal/contract-labor"
              element={
                <ProtectedRoute portalGuard="contract_labor">
                  <ContractLaborPortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ContractLaborDashboard />} />
              <Route path="work-orders" element={<ContractLaborWorkOrders />} />
              <Route path="log-hours" element={<ContractLaborLogHours />} />
              <Route path="documents" element={<ContractLaborDocuments />} />
              <Route path="comms" element={<Comms />} />
            </Route>
            {/* Customer Portal */}
            <Route
              path="/portal/customer"
              element={
                <ProtectedRoute portalGuard="customer">
                  <CustomerPortalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<CustomerDashboard />} />
              <Route path="projects" element={<CustomerProjects />} />
              <Route path="projects/:id" element={<CustomerProjectDetail />} />
              <Route path="invoices" element={<CustomerInvoices />} />
              <Route path="change-orders" element={<CustomerChangeOrders />} />
              <Route path="approvals" element={<CustomerApprovals />} />
              <Route path="documents" element={<CustomerDocuments />} />
              <Route path="comms" element={<Comms />} />
              <Route path="profile" element={<CustomerProfile />} />
            </Route>
            {/* Platform Admin */}
            <Route
              path="/platform"
              element={
                <PlatformAdminGuard>
                  <PlatformAdminDashboard />
                </PlatformAdminGuard>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
      </ImpersonationProvider>
      </ThemeProvider>
    </AuthProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
